import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { BRAND, SERVICES } from "../lib/brand";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { breadcrumbLd, serviceLd } from "../components/Seo";
import { Phone, CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const related = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);

  return (
    <div data-testid={`service-page-${service.slug}`} className="min-h-screen bg-white overflow-x-hidden">
      <Seo
        title={`${service.title} - South Florida`}
        description={`${service.heroSub} ${service.intro?.slice(0, 130)}...`}
        canonical={`/services/${service.slug}`}
        image={service.img}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: service.title, path: `/services/${service.slug}` },
          ]),
          serviceLd(service),
        ]}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-[110px] md:pt-[140px] pb-14 md:pb-24 bg-gradient-to-b from-[color:var(--dbe-cream)] to-white overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-[color:var(--dbe-red)]/5 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link
              to="/services"
              data-testid="service-back-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[color:var(--dbe-red)] hover:underline"
            >
              <i className="fa-solid fa-arrow-left text-[10px]" /> All Services
            </Link>
            <div className="mt-4 inline-flex items-center gap-2 bg-[color:var(--dbe-red)]/10 text-[color:var(--dbe-red)] rounded-full px-3 py-1.5 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase">
              <ShieldCheck className="w-3.5 h-3.5" />
              {service.tag} · Since {BRAND.since}
            </div>
            <h1
              data-testid="service-headline"
              className="mt-5 font-bebas leading-[0.95] text-[clamp(2.75rem,7vw,6rem)] text-neutral-900"
            >
              {service.heroHeadline}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-neutral-700 leading-relaxed max-w-xl">
              {service.heroSub}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/#quote"
                data-testid="service-cta-quote"
                className="inline-flex items-center justify-center gap-2 bg-[color:var(--dbe-red)] hover:bg-[color:var(--dbe-red-deep)] text-white px-6 py-3.5 rounded-full font-bold uppercase tracking-wider text-xs sm:text-sm shadow-[0_15px_35px_-15px_rgba(227,30,36,0.55)]"
              >
                Get a Free Estimate <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={`tel:${BRAND.phoneTel}`}
                data-testid="service-cta-call"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-neutral-900/10 hover:border-[color:var(--dbe-red)]/40 text-neutral-900 px-6 py-3.5 rounded-full font-bebas text-xl sm:text-2xl tracking-wide"
              >
                <Phone className="w-5 h-5 text-[color:var(--dbe-red)]" /> {BRAND.phoneDisplay}
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-[24px] sm:rounded-[32px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(15,15,25,0.35)] ring-1 ring-neutral-900/5">
              <img
                data-testid="service-hero-image"
                src={service.img}
                alt={service.title}
                className="w-full h-[320px] sm:h-[420px] lg:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] opacity-90">{service.tag}</div>
                  <div className="mt-1 font-bebas text-2xl sm:text-3xl">{service.title}</div>
                </div>
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                  <i className={`fa-solid ${service.icon} text-white text-lg`} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro + bullets */}
      <section className="relative py-14 sm:py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 grid lg:grid-cols-3 gap-10 lg:gap-14">
          <div className="lg:col-span-2">
            <p className="text-[color:var(--dbe-red)] text-xs tracking-[0.35em] uppercase font-bold">Overview</p>
            <p
              data-testid="service-intro"
              className="mt-4 text-lg sm:text-xl text-neutral-800 leading-relaxed"
            >
              {service.intro}
            </p>

            {service.sections?.map((sec, i) => (
              <div key={i} className="mt-10">
                <h2 className="font-bebas text-2xl sm:text-3xl md:text-4xl text-neutral-900 tracking-wide">
                  {sec.title}
                </h2>
                <p className="mt-3 text-base sm:text-lg text-neutral-700 leading-relaxed">
                  {sec.text}
                </p>
              </div>
            ))}
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-28 bg-[color:var(--dbe-cream)] border border-neutral-200 rounded-2xl p-6 sm:p-7 shadow-[0_20px_50px_-30px_rgba(15,15,25,0.2)]">
              <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--dbe-red)] font-bold">What's included</div>
              <ul className="mt-4 space-y-3">
                {service.bullets?.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-neutral-800 text-sm sm:text-base">
                    <CheckCircle2 className="w-5 h-5 text-[color:var(--dbe-red)] flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-neutral-200">
                <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-semibold">Talk to us</div>
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  className="mt-2 block font-bebas text-3xl sm:text-4xl text-[color:var(--dbe-red)] leading-none"
                >
                  {BRAND.phoneDisplay}
                </a>
                <Link
                  to="/#quote"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[color:var(--dbe-red)] hover:bg-[color:var(--dbe-red-deep)] text-white px-5 py-3 rounded-full font-bold uppercase tracking-wider text-xs sm:text-sm"
                >
                  Request Free Estimate <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Red CTA banner */}
      <section className="relative bg-[color:var(--dbe-red)] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage:
            "repeating-linear-gradient(-45deg, rgba(255,255,255,0.25) 0 2px, transparent 2px 18px)",
        }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 py-14 sm:py-16 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
          <div className="text-center md:text-left">
            <div className="text-[10px] sm:text-xs tracking-[0.3em] uppercase opacity-80">No obligation.</div>
            <h2 className="mt-2 font-bebas text-3xl sm:text-4xl md:text-5xl leading-[1.02] tracking-wide">
              Send {service.title.toLowerCase()} packing.
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <a
              href={`tel:${BRAND.phoneTel}`}
              className="inline-flex items-center justify-center gap-3 bg-white text-[color:var(--dbe-red)] px-6 sm:px-7 py-3.5 sm:py-4 rounded-full font-bebas text-xl sm:text-2xl tracking-wide"
            >
              <Phone className="w-5 h-5" /> {BRAND.phoneDisplay}
            </a>
            <Link
              to="/#quote"
              className="inline-flex items-center justify-center gap-2 bg-black/30 hover:bg-black/50 border border-white/40 text-white px-6 sm:px-7 py-3.5 sm:py-4 rounded-full font-bold uppercase tracking-wider text-xs sm:text-sm"
            >
              Free Estimate <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="relative py-14 sm:py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
            <div>
              <p className="text-[color:var(--dbe-red)] text-xs tracking-[0.35em] uppercase font-bold">Other Services</p>
              <h2 className="mt-3 font-bebas text-3xl sm:text-4xl md:text-5xl text-neutral-900">
                What else we handle
              </h2>
            </div>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-[color:var(--dbe-red)] text-sm font-bold uppercase tracking-wider"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {related.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-service-${s.slug}`}
                className="group relative overflow-hidden rounded-2xl bg-white border border-neutral-200 hover:border-[color:var(--dbe-red)]/60 hover:-translate-y-1 transition-all duration-300 shadow-[0_20px_50px_-30px_rgba(15,15,25,0.15)]"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute top-4 left-4 bg-[color:var(--dbe-red)] text-white text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full shadow">
                    {s.tag}
                  </div>
                  <div className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-white shadow flex items-center justify-center group-hover:bg-[color:var(--dbe-red)] transition-colors">
                    <i className={`fa-solid ${s.icon} text-[color:var(--dbe-red)] group-hover:text-white transition-colors`} />
                  </div>
                </div>
                <div className="p-5 sm:p-6">
                  <h3 className="font-bebas text-xl sm:text-2xl tracking-wide text-neutral-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed line-clamp-2">{s.blurb}</p>
                  <div className="mt-4 inline-flex items-center gap-2 text-[color:var(--dbe-red)] text-xs font-bold uppercase tracking-wider">
                    Learn More <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
